'use client'

import { toast } from '@/core/lib/toast'

import { go } from '@/features/veluvit/lib/navigation'

import type { User } from '@/features/veluvit/types/api.types'

/* 소셜 로그인 왕복 — 동의 화면으로 페이지를 떠났다가 서버가 정한 주소(SOCIAL_LOGIN_REDIRECT_URL)로 돌아온다.
   떠나기 전에 어디서·무엇으로 떠났는지 적어 두고, 돌아와 초기 데이터를 받은 뒤 결과를 알리고 원래 화면으로 보낸다.
   서버 세션이 진실이다 — 적어 둔 기록은 안내 문구와 복귀 위치에만 쓴다. */

const KEY = 'social_login'
/** 이보다 오래된 기록은 버린다(동의 화면에서 한참 머물다 포기한 경우) */
const TTL_MS = 15 * 60 * 1000
const LABEL: Record<string, string> = {
  kakao: '카카오',
  google: 'Google',
  apple: 'Apple',
}

/** 서버 콜백 오류 코드 → 안내 문구 (백엔드 정의서 2026-09-29 소셜 로그인 오류 목록) */
const ERROR_TEXT: Record<string, string> = {
  access_denied: '로그인을 취소했어요.',
  invalid_state: '로그인 시간이 지났어요. 다시 시도해 주세요.',
  account_link_required:
    '같은 이메일로 가입한 계정이 있어요. 그 계정으로 로그인한 뒤 연결해 주세요.',
  already_connected: '이미 다른 계정에 연결된 로그인이에요.',
  account_conflict:
    '다른 계정과 겹쳐 로그인할 수 없어요. 고객센터로 문의해 주세요.',
  inactive_account: '사용할 수 없는 계정이에요. 고객센터로 문의해 주세요.',
  session_changed: '로그인 중에 상태가 바뀌었어요. 다시 시도해 주세요.',
  social_upstream:
    '로그인 서비스에 연결하지 못했어요. 잠시 후 다시 시도해 주세요.',
  not_configured: '지금은 이 방법으로 로그인할 수 없어요.',
}

type Pending = { provider: string; back: string; at: number }

function read(): Pending | null {
  try {
    const raw = window.sessionStorage.getItem(KEY)
    window.sessionStorage.removeItem(KEY)
    const value = raw ? (JSON.parse(raw) as Partial<Pending>) : null
    if (
      !value ||
      typeof value.provider !== 'string' ||
      typeof value.back !== 'string' ||
      typeof value.at !== 'number'
    )
      return null
    return Date.now() - value.at < TTL_MS ? (value as Pending) : null
  } catch {
    return null
  }
}

/** 동의 화면으로 떠난다 — 지금 화면을 적어 두고 페이지를 옮긴다(fetch 아님) */
export function beginSocialLogin(provider: string, authorizeUrl: string) {
  try {
    const pending: Pending = {
      provider,
      back: window.location.pathname + window.location.search,
      at: Date.now(),
    }
    window.sessionStorage.setItem(KEY, JSON.stringify(pending))
  } catch {
    // 저장이 막힌 브라우저(사생활 보호 모드 등)는 복귀 안내만 빠진다 — 로그인 자체는 서버 세션으로 된다
  }
  window.location.assign(authorizeUrl)
}

/**
 * 돌아온 뒤 초기 데이터를 받고 한 번 부른다. 결과를 알리고, 떠났던 화면이 따로 있으면 그리로 보낸다.
 * 서버가 실패를 `?social_error=코드`로 넘기면 그 문구를 보인다(백엔드에 요청한 복귀 규약).
 */
export function finishSocialLogin(user: User | null) {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  const code = url.searchParams.get('social_error')
  const pending = read()
  if (code) {
    url.searchParams.delete('social_error')
    window.history.replaceState(
      window.history.state,
      '',
      url.pathname + url.search + url.hash
    )
    toast.show(
      ERROR_TEXT[code] || '로그인을 마치지 못했어요. 다시 시도해 주세요.',
      { duration: 3600 }
    )
    // 실패해도 서버는 첫 화면(/)으로 돌려보낸다 — 떠났던 화면으로 되돌려 거기서 다시 시도하게
    if (pending?.back && pending.back !== url.pathname + url.search)
      go(pending.back)
    return
  }
  if (!pending) return
  const label = LABEL[pending.provider] || '간편 로그인'
  if (user && !user.guest) {
    toast.show(`${label}로 로그인했어요`)
    if (pending.back && pending.back !== url.pathname + url.search)
      go(pending.back)
  } else {
    toast.show(`${label} 로그인을 마치지 못했어요. 다시 시도해 주세요.`, {
      duration: 3200,
    })
  }
}
